import type { ImportApplyResult, ImportBatch, ImportBatchStatusPayload, ImportEntityUpdatePayload, ImportInstruction, ImportProvider, ImportRowUpdatePayload } from "../types";
import { api, apiBlob, apiFormData, ApiError } from "./core";

export const importsApi = {
  getImportInstruction: (provider: ImportProvider) =>
    api<ImportInstruction | null>(`/import-instructions?provider=${encodeURIComponent(provider)}`),
  clearImportCategoryCache: (groupId: string) =>
    api<void>("/imports/category-cache", { method: "DELETE", groupId }),
  deleteImportCategoryCacheEntry: (groupId: string, cacheId: string) =>
    api<void>(`/imports/category-cache/${cacheId}`, { method: "DELETE", groupId }),
  exportImportCategoryCache: (groupId: string) =>
    apiBlob("/debug/category-cache/export", { groupId }),
  importImportCategoryCache: (groupId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiFormData<{ created_count: number; updated_count: number }>("/debug/category-cache/import", formData, { groupId });
  },
  createStatementImport: (groupId: string, payload: { file: File; provider: ImportProvider; sourceAccountId: string; asyncProcessing?: boolean }) => {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("provider", payload.provider);
    formData.append("source_account_id", payload.sourceAccountId);
    if (payload.asyncProcessing) formData.append("async_processing", "true");
    return apiFormData<ImportBatch>("/imports/statements", formData, { groupId });
  },
  createFileImport: (
    groupId: string,
    payload: { file: File; includeRecords: boolean; includeCategories: boolean; includeAccounts: boolean; includeCurrencies: boolean; asyncProcessing?: boolean },
  ) => {
    const formData = new FormData();
    formData.append("file", payload.file);
    formData.append("include_records", String(payload.includeRecords));
    formData.append("include_categories", String(payload.includeCategories));
    formData.append("include_accounts", String(payload.includeAccounts));
    formData.append("include_currencies", String(payload.includeCurrencies));
    if (payload.asyncProcessing) formData.append("async_processing", "true");
    return apiFormData<ImportBatch>("/imports/files", formData, { groupId });
  },
  importBatches: async (groupId: string, options: { limit?: number; includeRows?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    if (options.includeRows) params.set("include_rows", "true");
    const query = params.toString();
    try {
      return await api<ImportBatch[]>(`/imports${query ? `?${query}` : ""}`, { groupId });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  importBatch: (groupId: string, batchId: string) =>
    api<ImportBatch>(`/imports/${batchId}`, { groupId }),
  importBatchStatus: (groupId: string, batchId: string) =>
    api<ImportBatchStatusPayload>(`/imports/${batchId}/status`, { groupId }),
  updateImportBatchSourceAccount: (groupId: string, batchId: string, sourceAccountId: string) =>
    api<ImportBatch>(`/imports/${batchId}`, { method: "PATCH", body: JSON.stringify({ source_account_id: sourceAccountId }), groupId }),
  stopImportBatch: (groupId: string, batchId: string) =>
    api<ImportBatch>(`/imports/${batchId}/stop`, { method: "POST", groupId }),
  deleteImportBatch: (groupId: string, batchId: string) =>
    api<void>(`/imports/${batchId}`, { method: "DELETE", groupId }),
  rollbackImportBatch: (groupId: string, batchId: string, payload?: { records: boolean; categories: boolean; accounts: boolean }) =>
    api<ImportBatch>(`/imports/${batchId}/rollback`, { method: "POST", body: JSON.stringify(payload || { records: true, categories: false, accounts: false }), groupId }),
  updateImportRow: (groupId: string, batchId: string, rowId: string, payload: ImportRowUpdatePayload) =>
    api<ImportBatch>(`/imports/${batchId}/rows/${rowId}`, { method: "PATCH", body: JSON.stringify(payload), groupId }),
  updateImportEntity: (groupId: string, batchId: string, entityId: string, payload: ImportEntityUpdatePayload) =>
    api<ImportBatch>(`/imports/${batchId}/entities/${entityId}`, { method: "PATCH", body: JSON.stringify(payload), groupId }),
  applyImportBatch: (groupId: string, batchId: string, options: { asyncProcessing?: boolean } = {}) =>
    api<ImportApplyResult>(`/imports/${batchId}/apply${options.asyncProcessing ? "?async_processing=true" : ""}`, { method: "POST", groupId }),
};
